<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';

import CardTypeIcon from './CardTypeIcon.vue';
import CardStageBadge from './CardStageBadge.vue';
import CardPriority from './CardPriority.vue';
import CardDueDate from './CardDueDate.vue';
import CardAssignees from './CardAssignees.vue';
import CardPropertyRow from './CardPropertyRow.vue';
import CardLabels from './CardLabels.vue';
import CardStatusBadge from './CardStatusBadge.vue';
import { PRIORITIES } from '../cardFilters';

const props = defineProps({
  card: { type: Object, required: true },
  stages: { type: Array, required: true },
  agents: { type: Array, required: true },
  teams: { type: Array, required: true },
  // The account's labels: { id, title, color }.
  labels: { type: Array, required: true },
});

const emit = defineEmits(['move', 'priority', 'assignee', 'team', 'label']);

// DropdownMenu rejects a falsy value, so "no team" needs a value of its own.
const NO_TEAM = 'none';

const { t } = useI18n();

const stageIndex = computed(() =>
  props.stages.findIndex(stage => stage.id === props.card.stage_id)
);

// DropdownMenu rejects a falsy value, so ids travel as strings.
const stageMenu = computed(() =>
  props.stages.map(stage => ({
    label: stage.name,
    value: String(stage.id),
    action: 'stage',
    isSelected: stage.id === props.card.stage_id,
  }))
);

const priorityMenu = computed(() =>
  PRIORITIES.map(priority => ({
    label: t(`CARDS.PRIORITY.${priority}`),
    value: priority,
    action: 'priority',
    isSelected: priority === props.card.priority,
  }))
);

const teamMenu = computed(() => [
  {
    label: t('CARDS.SHOW.NO_TEAM'),
    value: NO_TEAM,
    action: 'team',
    isSelected: !props.card.team,
  },
  ...props.teams.map(team => ({
    label: team.name,
    value: String(team.id),
    action: 'team',
    isSelected: team.id === props.card.team?.id,
  })),
]);

const onTeam = ({ value }) =>
  emit('team', value === NO_TEAM ? null : Number(value));

const assigneeIds = computed(() => props.card.assignees.map(user => user.id));

const assigneeMenu = computed(() =>
  props.agents.map(agent => ({
    label: agent.name,
    value: String(agent.id),
    action: 'assignee',
    thumbnail: { name: agent.name, src: agent.thumbnail },
    isSelected: assigneeIds.value.includes(agent.id),
  }))
);
</script>

<template>
  <section class="flex flex-col gap-1">
    <!-- Changed through the Resolver / Cancelar / Reabrir buttons of the modal header. -->
    <CardPropertyRow
      :label="$t('CARDS.SHOW.STATUS')"
      data-testid="card-property-status"
    >
      <CardStatusBadge
        :status="card.status"
        :labels="card.pipeline_status_labels"
      />
    </CardPropertyRow>
    <CardPropertyRow
      :label="$t('CARDS.SHOW.STAGE')"
      :menu-items="stageMenu"
      data-testid="card-property-stage"
      @select="emit('move', Number($event.value))"
    >
      <CardStageBadge
        v-if="stageIndex >= 0"
        :stage="stages[stageIndex]"
        :index="stageIndex"
      />
    </CardPropertyRow>
    <CardPropertyRow
      :label="$t('CARDS.SHOW.PRIORITY')"
      :menu-items="priorityMenu"
      data-testid="card-property-priority"
      @select="emit('priority', $event.value)"
    >
      <CardPriority :priority="card.priority" show-label />
    </CardPropertyRow>
    <CardPropertyRow
      :label="$t('CARDS.SHOW.ASSIGNEES')"
      :menu-items="assigneeMenu"
      multiple
      data-testid="card-property-assignees"
      @select="emit('assignee', Number($event.value))"
    >
      <CardAssignees :assignees="card.assignees" />
      <span v-if="card.assignees.length === 1" class="truncate">
        {{ card.assignees[0].name }}
      </span>
    </CardPropertyRow>
    <CardPropertyRow :label="$t('CARDS.SHOW.DUE_AT')">
      <CardDueDate v-if="card.due_at" :card="card" class="-mx-1.5" />
      <span v-else class="text-n-slate-10">
        {{ $t('CARDS.SHOW.NO_DUE') }}
      </span>
    </CardPropertyRow>
    <CardPropertyRow
      v-if="card.task_summary.first?.type_name"
      :label="$t('CARDS.SHOW.TYPE')"
    >
      <CardTypeIcon :icon="card.task_summary.first.type_icon" />
      <span class="truncate">{{ card.task_summary.first.type_name }}</span>
    </CardPropertyRow>
    <CardPropertyRow
      :label="$t('CARDS.SHOW.TEAM')"
      :menu-items="teamMenu"
      data-testid="card-property-team"
      @select="onTeam"
    >
      <span v-if="card.team" class="truncate">{{ card.team.name }}</span>
      <span v-else class="text-n-slate-10">
        {{ $t('CARDS.SHOW.NO_TEAM') }}
      </span>
    </CardPropertyRow>
    <CardPropertyRow :label="$t('CARDS.SHOW.SOURCE')">
      {{ $t(`CARDS.SOURCE.${card.source}`) }}
    </CardPropertyRow>
    <CardPropertyRow
      :label="$t('CARDS.SHOW.LABELS')"
      class="!items-start"
      data-testid="card-property-labels"
    >
      <CardLabels
        :selected="card.labels"
        :labels="labels"
        @toggle="emit('label', $event)"
      />
    </CardPropertyRow>
    <CardPropertyRow :label="$t('CARDS.SHOW.CREATED')">
      {{ format(new Date(card.created_at), "dd/MM/yyyy 'às' HH:mm") }}
    </CardPropertyRow>
  </section>
</template>
